/**
 * Loyalty Auto-Login API Service
 *
 * Handles automatic user login via `ltkn` URL parameter (external loyalty token).
 * Mirrors Laravel middleware `externalloyaltyconnect` logic on the React side.
 *
 * Flow:
 *   1. User visits URL with ?ltkn={token}
 *   2. Get PartnerKey for the hotel from accommodationGroupDetail
 *   3. POST to apiservices.bookingwhizz.com with directToken + Partner-Api-Key
 *   4. On success: store user profile + token in localStorage
 *   5. React UI picks up logged-in state via localStorage
 */

import axios from 'axios';
import { saveUserSession, saveLoyaltyDiscount, clearLoyaltyDiscount, getUserSession, isUserLoggedIn } from '../utils/auth';
import { fetchRedeemOverrideRules } from './loyaltyApi';

const EXTERNAL_LOYALTY_API_URL = 'https://apiservices.bookingwhizz.com/api/member/accessToken?debug=1';

/**
 * Call external loyalty API to validate/connect with directToken
 * Equivalent of Laravel `externalloyaltyconnect($directToken, $partnerApiKey)`
 *
 * @param {string} directToken - Login token (ltkn URL param)
 * @param {string} partnerApiKey - Partner API key for the hotel
 * @returns {Promise<Object>} { status, profile, data } from external loyalty API
 */
export const externalLoyaltyConnect = async (directToken, partnerApiKey) => {
  if (!directToken) {
    throw new Error('directToken is required');
  }
  if (!partnerApiKey) {
    throw new Error('partnerApiKey is required');
  }

  // Use FormData to match Laravel curl behavior (which sends as multipart when array)
  const formData = new FormData();
  formData.append('directToken', directToken);

  try {
    const response = await axios.post(
      EXTERNAL_LOYALTY_API_URL,
      formData,
      {
        headers: {
          'Partner-Api-Key': partnerApiKey,
          'Accept': 'application/json',
        },
        timeout: 30000,
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || `Loyalty API error: ${error.response.status}`);
    }
    if (error.request) {
      throw new Error('Network error: Failed to reach loyalty service');
    }
    throw new Error(error.message || 'Unknown loyalty API error');
  }
};

/**
 * Read PartnerKey from accommodationGroupDetail (same priority as AuthModal)
 *
 * @param {Object} accommodationGroupDetail
 * @returns {string|null}
 */
export const getPartnerKeyFromGroup = (accommodationGroupDetail) => {
  if (!accommodationGroupDetail) return null;

  if (Array.isArray(accommodationGroupDetail.accommodationDetails) &&
      accommodationGroupDetail.accommodationDetails.length > 0) {
    const first = accommodationGroupDetail.accommodationDetails[0];
    return first?.PartnerKey ||
           first?.partnerKey ||
           first?.partner_key ||
           null;
  }

  return accommodationGroupDetail?.PartnerKey ||
         accommodationGroupDetail?.partnerKey ||
         accommodationGroupDetail?.partner_key ||
         null;
};

/**
 * Process auto-login if `ltkn` is present in URL
 * Should be called from main components (DesktopComponent, AvailabilityComponent)
 * once accommodationGroupDetail is loaded.
 *
 * @param {URLSearchParams} searchParams - React Router search params
 * @param {Object} accommodationGroupDetail - Loaded from useAccommodationGroupDetail hook
 * @returns {Promise<Object|null>} Login result or null
 */
export const processAutoLogin = async (searchParams, accommodationGroupDetail) => {
  const ltkn = searchParams?.get?.('ltkn');
  if (!ltkn) return null;

  // Avoid duplicate API calls — but always ensure loyalty discount is fetched
  const lastProcessedLtkn = sessionStorage.getItem('lastAutoLoginLtkn');
  const loyaltyAlreadySaved = localStorage.getItem('loyaltyDiscount');
  if (lastProcessedLtkn === ltkn && loyaltyAlreadySaved) {
    const userSession = localStorage.getItem('userSession');
    if (userSession) {
      try {
        const session = JSON.parse(userSession);
        if (session?.user) {
          return { status: true, profile: session.user.profile, cached: true };
        }
      } catch (e) {
        // fall through to fetch again
      }
    }
  } else if (lastProcessedLtkn === ltkn) {
    sessionStorage.removeItem('lastAutoLoginLtkn');
  }

  const partnerApiKey = getPartnerKeyFromGroup(accommodationGroupDetail);
  if (!partnerApiKey) {
    return null;
  }

  try {
    const result = await externalLoyaltyConnect(ltkn, partnerApiKey);

    if (result?.status === true && result?.profile) {
      // Store auth state using same utility that AuthModal uses
      const newToken = result?.data?.directToken || ltkn;
      saveUserSession(result, newToken);

      if (result.profile?.GroupId) {
        localStorage.setItem('loggedInGroupId', String(result.profile.GroupId));
      }

      sessionStorage.setItem('lastAutoLoginLtkn', ltkn);

      // Fetch loyalty discount rules (same priority as AuthModal manual login flow)
      try {
        const hotelId = searchParams?.get?.('hotel_id') || searchParams?.get?.('accommodationid');

        // EXACT priority from AuthModal.jsx lines 197-211 — CrmProfileId FIRST
        let profileId = null;

        // 1. Try profile array
        if (Array.isArray(result.profile) && result.profile.length > 0) {
          const masterProfile = result.profile.find(p => p.masterProfile || p.MasterProfile) || result.profile[0];
          profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId ||
                      masterProfile?.MasterProfileId || masterProfile?.masterProfileId ||
                      masterProfile?.id || masterProfile?.ID;
        }
        // 2. Try profile object
        else if (result.profile && typeof result.profile === 'object') {
          profileId = result.profile.CrmProfileId || result.profile.crmProfileId ||
                      result.profile.MasterProfileId || result.profile.masterProfileId ||
                      result.profile.id || result.profile.ID;
        }

        // 3. Fallback to data object (nested profile or direct)
        if (!profileId && result.data) {
          if (Array.isArray(result.data.profile) && result.data.profile.length > 0) {
            const masterProfile = result.data.profile.find(p => p.masterProfile || p.MasterProfile) || result.data.profile[0];
            profileId = masterProfile?.CrmProfileId || masterProfile?.crmProfileId ||
                        masterProfile?.MasterProfileId || masterProfile?.masterProfileId ||
                        masterProfile?.id || masterProfile?.ID;
          } else if (result.data.profile && typeof result.data.profile === 'object') {
            profileId = result.data.profile.CrmProfileId || result.data.profile.crmProfileId ||
                        result.data.profile.MasterProfileId || result.data.profile.masterProfileId ||
                        result.data.profile.id || result.data.profile.ID;
          }
          profileId = profileId || result.data.CrmProfileId || result.data.crmProfileId ||
                      result.data.MasterProfileId || result.data.masterProfileId ||
                      result.data.id || result.data.ID;
        }

        if (hotelId && profileId) {
          try {
            const loyaltyResponse = await fetchRedeemOverrideRules({
              accommodationId: hotelId,
              profileId: profileId
            });
            if (loyaltyResponse && loyaltyResponse.success) {
              saveLoyaltyDiscount(loyaltyResponse);
            }
          } catch (e) {
            // Redeem rule may not exist — silently skip (login still works)
          }
        }
      } catch (loyaltyError) {
        // Silent — login flow still completes
      }

      // Notify Header + other components to refresh login state
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: result }));
      window.dispatchEvent(new CustomEvent('loyaltyAutoLogin', { detail: result }));

      return result;
    }

    if (result?.status === false) {
      console.warn('[AutoLogin] External loyalty rejected token:', result?.message || 'unknown');
    }

    return result;
  } catch (error) {
    console.error('[AutoLogin] Failed:', error.message);
    return null;
  }
};

/**
 * Clear auto-login state (e.g., on logout)
 * Note: For full logout, prefer clearUserSession from utils/auth.js
 */
export const clearAutoLoginState = () => {
  localStorage.removeItem('userSession');
  localStorage.removeItem('authToken');
  localStorage.removeItem('loggedInGroupId');
  sessionStorage.removeItem('lastAutoLoginLtkn');
};

/**
 * Extract the CRM/Master profile id from the saved user session.
 * Mirrors the priority chain used during auto-login (CrmProfileId first).
 */
const extractProfileIdFromSession = () => {
  const session = getUserSession();
  const result = session?.user || session;
  if (!result) return null;

  let profileId = null;

  // 1. profile array
  if (Array.isArray(result.profile) && result.profile.length > 0) {
    const mp = result.profile.find(p => p.masterProfile || p.MasterProfile) || result.profile[0];
    profileId = mp?.CrmProfileId || mp?.crmProfileId || mp?.MasterProfileId || mp?.masterProfileId || mp?.id || mp?.ID;
  }
  // 2. profile object
  else if (result.profile && typeof result.profile === 'object') {
    profileId = result.profile.CrmProfileId || result.profile.crmProfileId ||
                result.profile.MasterProfileId || result.profile.masterProfileId ||
                result.profile.id || result.profile.ID;
  }

  // 3. fallback to data object (nested profile or direct)
  if (!profileId && result.data) {
    if (Array.isArray(result.data.profile) && result.data.profile.length > 0) {
      const mp = result.data.profile.find(p => p.masterProfile || p.MasterProfile) || result.data.profile[0];
      profileId = mp?.CrmProfileId || mp?.crmProfileId || mp?.MasterProfileId || mp?.masterProfileId || mp?.id || mp?.ID;
    } else if (result.data.profile && typeof result.data.profile === 'object') {
      profileId = result.data.profile.CrmProfileId || result.data.profile.crmProfileId ||
                  result.data.profile.MasterProfileId || result.data.profile.masterProfileId ||
                  result.data.profile.id || result.data.profile.ID;
    }
    profileId = profileId || result.data.CrmProfileId || result.data.crmProfileId ||
                result.data.MasterProfileId || result.data.masterProfileId ||
                result.data.id || result.data.ID;
  }

  return profileId;
};

/**
 * Re-fetch the logged-in member's redeem-override (member discount) for the
 * CURRENT hotel and refresh the cached loyaltyDiscount accordingly.
 *
 * Why: the loyaltyDiscount cache is only set once at login (for the hotel that
 * was active then). When the user switches hotels, that stale discount would
 * otherwise leak onto a different hotel. This refetches per hotel so each hotel
 * shows its own member discount (or none).
 *
 * Behavior:
 *   - Valid override for this hotel (Value > 0) → update cache
 *   - Hotel has no override (API responds with no/zero value) → clear cache
 *   - Network/HTTP error (throw) → leave cache untouched (avoid wiping a valid
 *     discount on a transient failure)
 *
 * Dispatches `loyaltyDiscountUpdated` so components can re-render with the
 * fresh value.
 *
 * @param {string|number} accommodationId - Current hotel/accommodation id
 */
export const refreshLoyaltyDiscountForHotel = async (accommodationId) => {
  try {
    if (!isUserLoggedIn() || !accommodationId) return;

    const profileId = extractProfileIdFromSession();
    if (!profileId) return;

    let loyaltyResponse;
    try {
      loyaltyResponse = await fetchRedeemOverrideRules({ accommodationId, profileId });
    } catch (e) {
      // Network/HTTP error — keep existing cache so a transient failure doesn't
      // remove a legitimate discount. (No cross-hotel leak fix here, but safe.)
      return;
    }

    const value = loyaltyResponse && loyaltyResponse.success && loyaltyResponse.data
      ? parseFloat(loyaltyResponse.data.Value || 0)
      : 0;

    if (value > 0) {
      saveLoyaltyDiscount(loyaltyResponse);
    } else {
      // This hotel has no member override — remove any stale discount carried
      // over from a previously viewed hotel.
      clearLoyaltyDiscount();
    }

    window.dispatchEvent(new Event('loyaltyDiscountUpdated'));
  } catch {
    // Never break the page for a discount refresh.
  }
};
